import React from 'react';
import { Text, Spinner, CardSurface } from '@spark';
import SpecificAILogo from '@/shared/assets/SpecificAILogo';

const BORDER = 'var(--Semantic-Border-default, #e2e2e9)';

const CELLS = [
    { key: 'truePositive', label: 'True Positive', color: '#389e0d', background: '#f6ffed' },
    { key: 'falsePositive', label: 'False Positive', color: '#d46b08', background: '#fff7e6' },
    { key: 'falseNegative', label: 'False Negative', color: '#cf1322', background: '#fff1f0' },
    { key: 'trueNegative', label: 'True Negative', color: '#1777ff', background: '#f0f7ff' },
];

const panelLayoutStyle = {
    flex: 1,
    minWidth: 0,
};

const ModelHeader = ({ title, modelType, color, isProcessing }) => {
    if (!title) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            {modelType === 'teacher' ? (
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
            ) : (
                <SpecificAILogo size="16px" style={{ marginRight: 0 }} />
            )}
            <Text variant="base-semibold" color="primary">{title}</Text>
            {isProcessing && <Spinner size="small" />}
        </div>
    );
};

const MatrixCard = ({ title, data, isProcessing, modelType, color, accuracyMetric }) => {
    const header = (
        <ModelHeader title={title} modelType={modelType} color={color} isProcessing={isProcessing} />
    );

    if (!data || isProcessing) {
        return (
            <CardSurface bordered={false} padding="large" style={panelLayoutStyle}>
                {header}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 240,
                }}>
                    <Text variant="base-regular" color="secondary">
                        {isProcessing ? 'Processing…' : 'No data available'}
                    </Text>
                </div>
            </CardSurface>
        );
    }

    const { truePositive, falsePositive, falseNegative, trueNegative } = data;
    const total = truePositive + falsePositive + falseNegative + trueNegative;

    const getPercentage = (value) =>
        total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

    const getAccuracyDisplay = () => {
        if (accuracyMetric !== null && accuracyMetric !== undefined) {
            return `${(accuracyMetric * 100).toFixed(1)}%`;
        }
        return total > 0
            ? `${(((truePositive + trueNegative) / total) * 100).toFixed(1)}%`
            : '0.0%';
    };

    return (
        <CardSurface bordered={false} padding="large" style={panelLayoutStyle}>
            {header}
            <div style={{ marginBottom: 16 }}>
                <Text variant="heading-2-semibold" color="primary" style={{ display: 'block', lineHeight: 1.2 }}>
                    {getAccuracyDisplay()}
                </Text>
                <Text variant="detail-regular" color="secondary">Overall accuracy</Text>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {CELLS.map(({ key, label, color: cellColor, background }) => {
                    const value = data[key];
                    return (
                        <div key={key} style={{ background, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px' }}>
                            <div style={{ fontSize: 22, fontWeight: 600, color: cellColor, lineHeight: 1.2 }}>
                                {value === null || value === undefined ? '-' : value}
                            </div>
                            <Text variant="detail-medium" color="primary">{label}</Text>
                            <Text variant="detail-regular" color="secondary" as="div">{getPercentage(value)}%</Text>
                        </div>
                    );
                })}
            </div>

            <div style={{
                marginTop: 16,
                padding: '10px 14px',
                background: 'var(--Semantic-Bg-secondary, #fafafb)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Text variant="detail-regular" color="secondary">Total samples</Text>
                <Text variant="detail-semibold" color="primary">{total}</Text>
            </div>
        </CardSurface>
    );
};

export default MatrixCard;
